/**
 * Modelo del AVISO GLOBAL de estado comercial (la barra que corona el panel).
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * QUÉ PROBLEMA RESUELVE
 *
 * Cuando el trial vence, el bot DEJA DE RESPONDER: `channelAllowed()` corta la cascada del webhook
 * y el mensaje del cliente queda guardado sin respuesta. Hasta ahora el panel no lo decía en
 * ningún lado: `status` se queda en `'trial'` para siempre (el vencimiento se CALCULA, no se
 * escribe), el canal sigue mostrando su punto verde de "Activo" porque `channels.status` es un
 * dato aparte, y el único cartel honesto vivía dentro de `/app/billing` — una pantalla a la que
 * nadie entra salvo que ya sospeche algo. Resultado: la cuenta se apaga en silencio y el cliente
 * se entera cuando pierde una venta.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * DE DÓNDE SALE EL DATO, Y POR QUÉ DE AHÍ
 *
 * De `channel_capacity_self` → `billing_active` / `billing_reason`, que en la base salen de
 * `billing_is_active()` / `billing_inactive_reason()`. Esas dos funciones son el espejo declarado
 * de `computeActive()` (`_shared/entitlements.ts`), que es EXACTAMENTE lo que decide si el bot
 * responde.
 *
 * Eso no es una comodidad: es la propiedad que sostiene el aviso. Un banner que calculara el
 * vencimiento por su cuenta podría decir "tu servicio está activo" mientras el webhook calla al
 * bot, o al revés. Acá no puede: si el aviso aparece, el bot está mudo, porque los dos leen la
 * misma regla. Si algún día cambia la semántica (la gracia de `past_due`, un estado nuevo), cambia
 * en un solo lugar y este aviso la hereda sin tocarse.
 *
 * ⚠️ NO agregar acá un cálculo propio de fechas. El día que este archivo necesite saber si un
 * trial venció, la respuesta es pedirle el dato a la base, no reimplementar la regla.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * LA REGLA QUE ESTE MÓDULO EXISTE PARA SOSTENER
 *
 * **El aviso de facturación se ELIGE, nunca se hereda.**
 *
 * Es la misma lección que dejó el bug de `ChannelUnavailableNotice` (PR #60): allá una cadena de
 * ternarios mandaba al `else` —el copy de facturación— todo motivo no contemplado, y a un cliente
 * al día con su canal sano se le dijo "tu suscripción no está activa". Falso, y encima lo mandaba
 * a revisar una plata que no tenía ningún problema.
 *
 * Acá la superficie es peor: es una barra en TODAS las pantallas. Por eso un motivo desconocido
 * cae en `NOTICE_UNKNOWN`, que describe el síntoma real y verificable ("el bot no está
 * respondiendo") sin atribuirle al cliente una deuda que nadie confirmó. Afirmar de más sobre la
 * plata de alguien sigue siendo el peor default posible.
 */

/**
 * Motivo de inactividad (`billing_inactive_reason` en la base) → qué aviso mostrar.
 *
 * Las cuatro claves son EXACTAMENTE los valores que puede devolver esa función. Si allá se agrega
 * un motivo nuevo y acá no, no se rompe nada ni se miente: cae en el aviso neutro.
 *
 * `tone` decide el color; `cta` a dónde se manda a la persona. `support` es deliberado en el caso
 * desconocido: si no sabemos por qué se apagó, mandarlo a pagar es adivinar.
 */
export const NOTICE_BY_REASON = {
  trial_expired: { tone: "down", copy: "trialExpired", cta: "billing" },
  paused: { tone: "down", copy: "paused", cta: "billing" },
  cancelled: { tone: "down", copy: "cancelled", cta: "billing" },
  past_due_expired: { tone: "down", copy: "pastDueExpired", cta: "billing" },
};

/** Se apagó y no sabemos por qué: se dice el síntoma, no se inventa la causa. */
export const NOTICE_UNKNOWN = { tone: "neutral", copy: "unknown", cta: "support" };

/** Billing es owner-only: el aviso puede informar a un admin, pero no ofrecerle una ruta prohibida. */
export function canManageBilling(role) {
  return role === "owner";
}

/**
 * ¿Qué aviso corresponde? `null` = ninguno (que es el caso normal y silencioso).
 *
 * ⚠️ EL ORDEN IMPORTA, y el `!== false` también.
 *
 * Primero se mira `billing_active`, NO el motivo. `channel_capacity` devuelve `billing_reason`
 * poblado en casos donde la cuenta SIGUE ACTIVA (`plan_unknown`, `quota_undeclared`: catálogo
 * incompleto, que frena conectar un canal nuevo pero no calla al bot). Interpretar el motivo sin
 * mirar antes si está activa pondría una barra de "tu servicio está caído" sobre una cuenta que
 * está funcionando perfecto.
 *
 * Y sólo un `false` EXPLÍCITO enciende el aviso. Sin datos, con la RPC todavía en vuelo, con un
 * `forbidden` de un agente o con una respuesta vieja en caché, la respuesta es no mostrar nada:
 * el default de una barra roja global tiene que ser el silencio.
 */
export function billingNotice(capacity) {
  if (!capacity) return null;
  if (capacity.billing_active !== false) return null;
  return NOTICE_BY_REASON[capacity.billing_reason] ?? NOTICE_UNKNOWN;
}

/* ═════════════════════════════════════════════════════════════════════════════════════════════
 * CUENTA REGRESIVA DEL TRIAL — avisar ANTES, que es lo único que evita el problema
 *
 * `billingNotice()` avisa cuando la cuenta YA dejó de operar. Sirve, pero llega tarde por
 * definición: cuando aparece, el bot lleva un rato mudo. Esto es la mitad preventiva.
 *
 * ── SOBRE LA EXCLUSIÓN MUTUA: LO QUE ES CIERTO Y LO QUE NO ──
 *
 * Es cierto que los dos avisos NUNCA se muestran juntos: `billingNotice()` exige
 * `billing_active === false` y esto exige `=== true`, así que para UN MISMO SNAPSHOT de capacidad
 * como mucho uno devuelve algo. Eso está testeado y se sostiene.
 *
 * ⚠️ Lo que NO es cierto —y una versión anterior de este comentario lo afirmaba— es que "no hay
 * ninguna ventana". Sí la hay, y viene de dos cosas que la pureza de estas funciones no puede
 * arreglar:
 *
 *   · **La capacidad está cacheada.** `billing_active` es una foto del momento en que respondió la
 *     RPC, no un valor vivo. Un panel abierto la sostiene hasta que se vuelva a consultar.
 *   · **Son dos relojes.** `billing_active` lo decidió el servidor con SU reloj; el restante de acá
 *     se calcula con el del navegador. Pueden discrepar en minutos.
 *
 * Consecuencia observable: entre que el navegador cruza el vencimiento y que la RPC confirma el
 * corte, no se muestra NINGÚN aviso. Es deliberado y es el lado correcto en el que fallar — si el
 * servidor todavía dice que la cuenta está activa, el bot efectivamente sigue respondiendo, y
 * afirmar "tu servicio está caído" con el reloj del cliente sería inventar un corte que no ocurrió.
 * `nextCheck()` acota esa ventana volviendo a preguntar; no la elimina.
 * ═════════════════════════════════════════════════════════════════════════════════════════════ */

/** Desde cuándo se avisa. 48 h da margen para pagar sin que el cartel se vuelva parte del paisaje. */
export const TRIAL_WARNING_WINDOW_HOURS = 48;

const HORA_MS = 60 * 60 * 1000;
const DIA_MS = 24 * HORA_MS;

/**
 * ¿Hay que avisar que el trial está por vencer? `null` = no.
 *
 * ── LA VENTANA, Y SUS BORDES EXACTOS ──
 *
 * Se avisa con `0 <= restante <= 48 h`. Los dos extremos son CERRADOS a propósito:
 *
 * · **48 h justas entran.** Es el borde de "faltan dos días"; excluirlo dejaría el primer aviso
 *   para 47:59:59, sin ninguna razón.
 * · **0 justo entra, y este es el importante.** `billing_is_active()` corta con `trial_ends_at <
 *   now`, o sea que en el instante EXACTO del vencimiento la cuenta todavía cuenta como activa.
 *   Si acá se excluyera el 0, ese instante no tendría ningún aviso: ni el preventivo (fuera de
 *   ventana) ni el de vencida (todavía activa). Un hueco de un milisegundo, pero un hueco.
 * · **Restante negativo NO entra**: ahí `billing_active` ya es `false` y el aviso corresponde a
 *   `billingNotice()`. La guarda de arriba lo cubre; el chequeo explícito queda como defensa.
 *
 * ── LOS DÍAS, Y POR QUÉ SE DICEN CON "MENOS DE" ──
 *
 * `días = ceil(restante / 24 h)` → 2, 1 ó 0. Una sola fórmula, sin casos especiales; el número
 * elige el tramo y el copy lo nombra por su COTA en horas: "en las próximas 48 horas" y "en las
 * próximas 24 horas".
 *
 * Que la cota sea superior no es un adorno: es lo que hace que la frase sea VERDADERA en todo el
 * rango que cubre. Con `ceil`, un restante de 25 h cae en el tramo de 2 — y "termina en 2 días"
 * sería mentira, mientras que "en las próximas 48 horas" es exacto. Un aviso que exagera el tiempo
 * disponible es peor que no avisar: relaja justo cuando hay que apurarse.
 *
 * Por eso también `días = 0` y `días = 1` comparten copy: con 0 restante "en las próximas 24 horas"
 * sigue siendo cierto, y evita una tercera variante que en la práctica sólo podría verse en el
 * milisegundo exacto del vencimiento.
 */
export function trialCountdown(capacity, nowMs = Date.now()) {
  if (!capacity) return null;

  // Sólo con la cuenta VIVA. Un `false` explícito es de `billingNotice()`; `undefined`/`null`
  // (RPC en vuelo, `forbidden`, contrato viejo) no muestra nada, igual que allá.
  if (capacity.billing_active !== true) return null;
  if (String(capacity.billing_status ?? "").toLowerCase() !== "trial") return null;

  const fin = Date.parse(capacity.trial_ends_at ?? "");
  if (!Number.isFinite(fin)) return null; // sin fecha utilizable no se inventa una cuenta regresiva

  const restante = fin - nowMs;
  if (restante < 0 || restante > TRIAL_WARNING_WINDOW_HOURS * HORA_MS) return null;

  const days = Math.ceil(restante / DIA_MS);
  return {
    tone: "warn",
    copy: days >= 2 ? "trialEnding48h" : "trialEnding24h",
    days,
    cta: "billing",
  };
}

/* ═════════════════════════════════════════════════════════════════════════════════════════════
 * CUÁNDO VOLVER A MIRAR
 *
 * Sin esto, todo lo de arriba es una foto: la capacidad se pide una vez al montar y el restante se
 * calcula sólo cuando React vuelve a renderizar. Un panel abierto —que es lo NORMAL en una
 * herramienta de trabajo, no el caso raro— cruza las 48 h, las 24 h y el vencimiento sin enterarse
 * de ninguno. El aviso preventivo que no aparece a tiempo no previene nada.
 *
 * Se programa el PRÓXIMO hito en vez de encuestar seguido: los instantes que importan se conocen
 * de antemano (son tres, derivados de `trial_ends_at`), así que despertar en cada uno da precisión
 * exacta con una fracción de las consultas de un `setInterval`.
 * ═════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Techo de una espera. Cumple dos funciones distintas:
 *
 *   1. `setTimeout` guarda el retardo en un entero de 32 bits: con más de ~24,8 días **dispara de
 *      inmediato**, y un hito lejano se volvería un bucle apretado. Un trial de 30 días arranca a
 *      28 días del primer hito, así que el caso es real, no teórico.
 *   2. Un panel abierto muchas horas revalida de a ratos sin encuestar.
 */
export const MAX_SLEEP_MS = 6 * HORA_MS;

/** Tras el vencimiento, cada cuánto se le vuelve a preguntar AL SERVIDOR si ya cortó. */
export const EXPIRY_RECHECK_MS = 60 * 1000;

/**
 * ¿Cuándo hay que volver a evaluar, y hace falta repreguntarle a la base?
 *
 * `null` = no hay nada que programar. Devuelve `{ delayMs, refetch }`.
 *
 * ⚠️ `refetch` es la pieza que evita el error grave: al vencer NO se voltea el aviso con el reloj
 * del navegador, se vuelve a consultar `channel_capacity_self` y manda lo que diga el servidor. Un
 * cliente con la hora adelantada mostraría "tu servicio está caído" mientras el bot sigue
 * respondiendo perfecto — y esa es justamente la afirmación que más caro sale equivocar.
 *
 * Si el servidor insiste en que la cuenta sigue activa (desfasaje de relojes), se reintenta cada
 * `EXPIRY_RECHECK_MS` en lugar de encadenar consultas: converge cuando el servidor cambia de
 * opinión, que es la única autoridad.
 */
export function nextCheck(capacity, nowMs = Date.now()) {
  if (!capacity) return null;

  // Ya caída: `billingNotice()` da el aviso terminal y no hay hito que esperar. Que una
  // reactivación se vea recién al recargar es aceptable; encuestar por si acaso, no.
  if (capacity.billing_active !== true) return null;
  if (String(capacity.billing_status ?? "").toLowerCase() !== "trial") return null;

  const fin = Date.parse(capacity.trial_ends_at ?? "");
  if (!Number.isFinite(fin)) return null;

  // Los tres instantes en que la pantalla tiene que cambiar, en orden.
  const hitos = [
    fin - TRIAL_WARNING_WINDOW_HOURS * HORA_MS, // aparece el aviso
    fin - DIA_MS,                               // cambia al copy de horas
    fin,                                        // vence: la palabra la tiene el servidor
  ];
  const proximo = hitos.find((hito) => hito > nowMs);

  // Pasado el vencimiento y con el servidor todavía diciendo "activa": o la capacidad quedó vieja,
  // o los relojes discrepan. Se repregunta, acotado.
  if (proximo === undefined) return { delayMs: EXPIRY_RECHECK_MS, refetch: true };

  const delayMs = Math.min(proximo - nowMs, MAX_SLEEP_MS);
  // Sólo se repregunta si al despertar el trial ya venció. Un despertar recortado por el techo
  // llega antes del hito y no necesita molestar a la base: alcanza con volver a renderizar.
  return { delayMs, refetch: nowMs + delayMs >= fin };
}
